import { stdObject } from "../data/types/stdObject";
import { useTranslation } from "react-i18next";
import { convertDictData } from "../utils/convertDictData";
import { colors } from "../data/colors";
import { memo, useEffect, useRef, useState } from "react";
import QuickSearchComp from "./QuickSearchComp";
import BasicButton from "./BasicButton";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";

const BasicInput = memo(
  ({
    code = "",
    type = "text",
    refId = null,
    label = "",
    value = "",
    maxLength = undefined,
    active = true,
    autoFocus = false,
    onFocus,
    onBlur,
    onChange,
    onKeyDown,
    btnClick = () => {},
    innerStyle = {},
    style = {},
    placeholder = "",
    options = [],
    rangeParams = {},
    isTable = false,
    suffix = "",
    quickData = {},
  }: {
    code?: string;
    type?: string;
    refId?: null;
    label?: string;
    value?: string;
    maxLength?: number | undefined;
    active?: boolean;
    innerStyle?: stdObject;
    style?: stdObject;
    autoFocus?: boolean;
    onFocus?: (v: any) => {};
    onBlur?: (v: any) => {};
    onChange?: (v: any) => {};
    onKeyDown?: (v: any) => {};
    btnClick?: (v?: any) => void;
    placeholder?: string;
    options?: Array<any>;
    rangeParams?: stdObject;
    isTable?: boolean | number;
    suffix?: string;
    quickData?: stdObject;
  }) => {
    const { t } = useTranslation();
    const [showPass, setShowPass] = useState(false);
    const refQuickPerson = useRef<HTMLInputElement>(null);
    const [quickPerson, setQuickPerson] = useState({
      code: "",
      open: false,
      search: "",
    });
    const loginRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
      if (autoFocus) {
        loginRef.current?.focus();
      }
    }, []);

    const showVisibility = () => {
      const Icon = showPass ? VisibilityIcon : VisibilityOffIcon;
      return (
        <Icon
          onClick={() => setShowPass(!showPass)}
          style={{
            color: showPass ? colors.settings : colors.grey,
            marginLeft: 5,
          }}
        />
      );
    };

    const insertInput = () => {
      switch (type) {
        case "info":
          return value ?? "";
        case "select":
          return (
            <select
              ref={refId}
              className="selectStyle"
              defaultValue={value}
              value={value}
              disabled={!active}
              style={{ border: `solid 1px ${colors.black}`, ...innerStyle }}
              onInput={onChange}
            >
              {options.map((e: stdObject, index: number) => (
                <option key={index} value={e.value}>
                  {e.label}
                </option>
              ))}
            </select>
          );
        case "textarea":
          return (
            <textarea
              ref={refId}
              className="textAreaStyle"
              disabled={!active}
              onChange={onChange}
              value={value}
              placeholder={placeholder}
              style={{
                border: `solid 1px ${colors.black}`,
                ...innerStyle,
              }}
            />
          );
        case "dictionary":
          let dictData: stdObject = convertDictData(value);
          return "data" in dictData && Object.keys(dictData.data).length > 0 ? (
            <table className="dictTable">
              <tbody onClick={active ? onChange : () => {}}>
                {Object.keys(dictData.data).map((e: string, index: number) => (
                  <tr key={index}>
                    {dictData.hideCodes && (
                      <td valign="top">
                        <b>{e}</b>
                      </td>
                    )}
                    <td>{dictData.data[e].name}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div
              onClick={active ? onChange : () => {}}
              style={{
                padding: 3,
                paddingLeft: 5,
                paddingRight: 5,
                backgroundColor: colors.green,
                color: colors.white,
              }}
            >
              {t("p.select")}
            </div>
          );
        default:
          return (
            <span
              style={{
                display: type === "passwordInput" ? "flex" : "inline",
                flexDirection: "row",
              }}
            >
              <input
                ref={refId || loginRef}
                className="inputStyle"
                type={
                  type === "passwordInput"
                    ? showPass
                      ? "text"
                      : "password"
                    : type === "innerButton"
                      ? "button"
                      : type === "integer"
                        ? "number"
                        : type
                }
                defaultValue={value}
                maxLength={maxLength}
                value={value}
                checked={Number(value) === 1}
                disabled={!active}
                onFocus={onFocus}
                onBlur={onBlur}
                onChange={
                  ["checkbox", "button", "innerButton"].includes(type)
                    ? () => {}
                    : onChange
                }
                onClick={
                  ["checkbox", "button", "innerButton"].includes(type)
                    ? quickData.search
                      ? () =>
                          setQuickPerson({
                            ...quickPerson,
                            code: quickData.type,
                            open: true,
                          })
                      : onChange
                    : () => {}
                }
                onKeyDown={onKeyDown}
                placeholder={placeholder}
                style={{
                  width: type === "checkbox" ? "auto" : "100%",
                  border: `solid 1px ${colors.black}`,
                  ...innerStyle,
                }}
                {...(() => {
                  const { ...rest } = rangeParams;
                  return rest;
                })()}
              />
              {type === "passwordInput" && showVisibility()}
            </span>
          );
      }
    };

    const writeSuffix = () => (
      <span style={{ marginLeft: 5, marginTop: 3 }}>{suffix}</span>
    );

    return isTable ? (
      ["label", "list", "tableButton"].includes(type) ? (
        <tr
          style={{
            backgroundColor: ["list", "tableButton"].includes(type)
              ? colors.white
              : colors.grey,
            color: ["list", "tableButton"].includes(type)
              ? colors.black
              : colors.white,
            ...style,
          }}
        >
          <td
            colSpan={typeof isTable === "number" ? isTable : 2}
            align={type === "list" ? "left" : "center"}
            style={{
              padding: 5,
              fontWeight: type === "tableButton" ? "normal" : "bolder",
            }}
          >
            {type === "list" && " • "}
            {type === "tableButton" ? (
              <BasicButton
                title={label}
                // Icon={e.Icon}
                color={innerStyle.color}
                onClick={btnClick}
              />
            ) : (
              label
            )}
          </td>
        </tr>
      ) : (
        <tr style={{ ...style }}>
          <td style={{ position: "relative", whiteSpace: "nowrap" }}>
            {label}
            {quickData.search && quickPerson.open && (
              <QuickSearchComp
                code={code}
                type={quickData.type}
                searchType={quickData.type}
                refInput={refQuickPerson}
                inputData={quickPerson}
                setInputData={setQuickPerson}
                quickData={quickData}
                updateFunction={onChange}
                newFunction={quickData.new}
                casualFunction={quickData.casual}
                cleanFunction={quickData.clean}
              />
            )}
          </td>
          <td width={"100%"}>
            {insertInput()}
            {suffix !== "" && writeSuffix()}
          </td>
        </tr>
      )
    ) : (
      <div style={{ marginBottom: 3, ...style }}>
        {label !== "" && <div className="inputLabel noSelect">{label}</div>}
        <div style={{ display: "flex", flexDirection: "row" }}>
          {insertInput()}
          {suffix !== "" && writeSuffix()}
        </div>
      </div>
    );
  },
);

export default BasicInput;
